import {
  alpha,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone'
import { styled } from '@mui/material/styles'
import { MdSettings } from 'react-icons/md'
import { useNavigate } from 'react-router'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import { accountNotificationsApi } from '../../../../../../../api'

// Mirrors `NotificationView` in
// `apps/api/crates/bloom-auth/src/notifications.rs`. Both channels are
// normalised onto this shape server-side.
type NotificationItem = {
  id: string
  channel: 'whatsapp' | 'email'
  status: string
  template: string
  subject?: string | null
  recipient: string
  created_at: string
  delivered_at?: string | null
  error_message?: string | null
}

type NotificationPayload = {
  items: NotificationItem[]
  next_cursor: string | null
}

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px;
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`,
)

// Cap displayed badge count at 99+ to avoid the chip widening on
// busy inboxes.
function formatBadgeCount(n: number): string | number {
  if (n <= 0) return 0
  if (n > 99) return '99+'
  return n
}

// Compact label derived from the delivery row. WhatsApp templates are
// opaque SIDs; the channel chip is the user-friendly cue.
function describeNotification(item: NotificationItem): {
  title: string
  body: string
} {
  if (item.channel === 'email') {
    return {
      title: item.subject?.trim() || 'Bericht per e-mail',
      body: `Naar ${item.recipient}`,
    }
  }
  return {
    title: 'WhatsApp-bericht',
    body: `Naar ${item.recipient}`,
  }
}

function timeAgo(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: nl })
  } catch {
    return ''
  }
}

function HeaderNotifications() {
  const ref = useRef<HTMLButtonElement | null>(null)
  const navigate = useNavigate()
  const [isOpen, setOpen] = useState<boolean>(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [errored, setErrored] = useState<boolean>(false)

  const refresh = useCallback(() => {
    setLoading(true)
    setErrored(false)
    accountNotificationsApi({ limit: 20 })
      .then((data: NotificationPayload) => {
        setItems(data?.items ?? [])
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('failed to load notifications:', err)
        setErrored(true)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleOpen = (): void => {
    setOpen(true)
    // Refresh when the user pops the menu — covers the case where new
    // delivery rows landed since mount (long-lived dashboards).
    refresh()
  }
  const handleClose = (): void => setOpen(false)

  const count = items.length

  return (
    <>
      <Tooltip arrow title="Notificaties">
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            badgeContent={formatBadgeCount(count)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <NotificationsActiveTwoToneIcon />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          sx={{ p: 2 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Notificaties</Typography>
          {count > 0 && (
            <Typography variant="caption" color="text.secondary">
              {count} {count === 1 ? 'bericht' : 'berichten'}
            </Typography>
          )}
        </Box>
        <Divider />
        <List
          sx={{
            p: 0,
            minWidth: 360,
            maxWidth: 420,
            maxHeight: 420,
            overflowY: 'auto',
          }}
        >
          {loading && items.length === 0 ? (
            <ListItem sx={{ p: 3, justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </ListItem>
          ) : errored ? (
            <ListItem sx={{ p: 2 }}>
              <Typography variant="body2" color="error">
                Notificaties tijdelijk niet beschikbaar.
              </Typography>
            </ListItem>
          ) : items.length === 0 ? (
            <ListItem sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Geen notificaties
              </Typography>
            </ListItem>
          ) : (
            items.map((item) => {
              const { title, body } = describeNotification(item)
              return (
                <ListItem
                  key={item.id}
                  sx={{
                    p: 2,
                    display: 'block',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Typography
                      sx={{ fontWeight: 'bold', flex: 1, wordBreak: 'break-word' }}
                    >
                      {title}
                    </Typography>
                    <Chip
                      label={item.channel === 'email' ? 'E-mail' : 'WhatsApp'}
                      size="small"
                      color={item.channel === 'email' ? 'primary' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, wordBreak: 'break-all' }}
                  >
                    {body}
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {timeAgo(item.created_at)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={
                        item.status === 'failed' ? 'error' : 'text.secondary'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {item.status}
                    </Typography>
                  </Box>
                </ListItem>
              )
            })
          )}
          <ListItem sx={{ p: 2 }}>
            <Button
              startIcon={<MdSettings />}
              variant="contained"
              onClick={() => {
                handleClose()
                navigate('/management/notificaties')
              }}
              style={{ width: '100%', borderRadius: '20px' }}
            >
              Notificaties beheren
            </Button>
          </ListItem>
        </List>
      </Popover>
    </>
  )
}

export default HeaderNotifications
